import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  ResponsiveContainer,
} from 'recharts';
import { moderationService } from '../services/moderationService';

interface ContentQualityAnalytics {
  period_days: number;
  total_flagged: number;
  by_content_type: Record<string, number>;
  by_priority: Record<string, number>;
  by_status: Record<string, number>;
  recent_trends: Record<string, any>;
}

interface ModerationActivityAnalytics {
  period_days: number;
  total_actions: number;
  actions_by_type: Record<string, number>;
  actions_by_moderator: Record<string, number>;
  response_times: {
    average_hours: number;
    median_hours: number;
  };
  workload_distribution: Record<string, any>;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const ModerationAnalytics: React.FC = () => {
  const [contentAnalytics, setContentAnalytics] = useState<ContentQualityAnalytics | null>(null);
  const [activityAnalytics, setActivityAnalytics] = useState<ModerationActivityAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState(30);

  useEffect(() => {
    loadAnalytics();
  }, [selectedPeriod]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const [contentData, activityData] = await Promise.all([
        moderationService.getContentQualityAnalytics(selectedPeriod),
        moderationService.getModerationActivityAnalytics(selectedPeriod),
      ]);
      
      setContentAnalytics(contentData);
      setActivityAnalytics(activityData);
      setError(null);
    } catch (err) {
      setError('Failed to load analytics data');
      console.error('Error loading analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatChartData = (data: Record<string, number>) => {
    return Object.entries(data).map(([key, value]) => ({
      name: key.replace('_', ' ').toUpperCase(),
      value,
    }));
  };

  const calculatePercentage = (value: number, total: number) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) : '0';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!contentAnalytics || !activityAnalytics) {
    return null;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Moderation Analytics
        </Typography>
        
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value as number)}
            label="Period"
          >
            <MenuItem value={7}>Last 7 days</MenuItem>
            <MenuItem value={30}>Last 30 days</MenuItem>
            <MenuItem value={90}>Last 90 days</MenuItem>
            <MenuItem value={365}>Last year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Flagged Content
              </Typography>
              <Typography variant="h4">
                {contentAnalytics.total_flagged}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Moderation Actions
              </Typography>
              <Typography variant="h4">
                {activityAnalytics.total_actions}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg Response Time
              </Typography>
              <Typography variant="h4">
                {activityAnalytics.response_times.average_hours.toFixed(1)}h
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                High Priority Items
              </Typography>
              <Typography variant="h4" color="error">
                {contentAnalytics.by_priority.high || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Content Type Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Flagged Content by Type
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={formatChartData(contentAnalytics.by_content_type)}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {formatChartData(contentAnalytics.by_content_type).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Priority Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Content by Priority Level
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={formatChartData(contentAnalytics.by_priority)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Moderation Actions */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Moderation Actions Taken
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={formatChartData(activityAnalytics.actions_by_type)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Status Overview */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Content Status Overview
              </Typography>
              <Box>
                {Object.entries(contentAnalytics.by_status).map(([status, count]) => (
                  <Box key={status} mb={2}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">
                        {status.replace('_', ' ').toUpperCase()}
                      </Typography>
                      <Typography variant="body2">
                        {count} ({calculatePercentage(count, contentAnalytics.total_flagged)}%)
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={parseFloat(calculatePercentage(count, contentAnalytics.total_flagged))}
                      color={status === 'pending' ? 'warning' : 'success'}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Moderator Performance */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Moderator Activity
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Moderator</TableCell>
                      <TableCell align="right">Actions Taken</TableCell>
                      <TableCell align="right">Percentage</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(activityAnalytics.actions_by_moderator).map(([moderator, actions]) => (
                      <TableRow key={moderator}>
                        <TableCell component="th" scope="row">
                          {moderator}
                        </TableCell>
                        <TableCell align="right">{actions}</TableCell>
                        <TableCell align="right">
                          {calculatePercentage(actions as number, activityAnalytics.total_actions)}%
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label="Active"
                            color="success"
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Response Time Metrics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Response Time Metrics
              </Typography>
              <Box>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2">Average Response Time:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {activityAnalytics.response_times.average_hours.toFixed(1)} hours
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2">Median Response Time:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {activityAnalytics.response_times.median_hours.toFixed(1)} hours
                  </Typography>
                </Box>
                
                {/* Response time target indicator */}
                <Box mt={3}>
                  <Typography variant="body2" gutterBottom>
                    Target: &lt; 24 hours
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, (24 / activityAnalytics.response_times.average_hours) * 100)}
                    color={activityAnalytics.response_times.average_hours <= 24 ? 'success' : 'error'}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Quality Indicators */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quality Indicators
              </Typography>
              <Box>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2">Resolution Rate:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {calculatePercentage(
                      contentAnalytics.by_status.resolved || 0,
                      contentAnalytics.total_flagged
                    )}%
                  </Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2">Pending Items:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="warning.main">
                    {contentAnalytics.by_status.pending || 0}
                  </Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2">High Priority Pending:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="error.main">
                    {contentAnalytics.by_priority.high || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ModerationAnalytics;